import {LoaderFunctionArgs, redirect} from '@remix-run/node';
import {getActiveGiveawaysForPartner, getPartnerFromSlug} from '~/services/partner-service.server';
import {Box, Container, Grid, VStack} from '@styled-system/jsx';
import {h1, h2} from '@styled-system/recipes';
import PromotionCard from '~/components/PromotionCard';
import {differenceInDays} from 'date-fns';
import {startCase} from 'lodash-es';
import Benefits from '~/components/Benefits';
import {getRecentWinners} from '~/services/winner-service';
import RecentWinners from '~/components/RecentWinners';
import {superjson, useSuperLoaderData} from '~shared/utils/data';

export const loader = async ({params}: LoaderFunctionArgs) => {
  const {partnerSlug} = params;

  if (!partnerSlug) {
    throw new Response(null, {
      status: 404,
      statusText: "Not Found",
    });
  }

  const partner = await getPartnerFromSlug(partnerSlug)

  if (!partner) {
    throw redirect('/partners');
  }

  const activeGiveaways = await getActiveGiveawaysForPartner(partner.id, 3);
  const recentWinners = await getRecentWinners(partner.id, undefined);

  return superjson({
    partner,
    activeGiveaways: activeGiveaways.map(g => ({
      id: g.id,
      imageSrc: g.imageUrl as string,
      title: g.title,
      endingSoon: differenceInDays(g.endDate, new Date()) <= 3,
      categories: g.categories.map(startCase),
    })),
    recentWinners: recentWinners.map(w => ({
      id: w.id,
      name: w.name,
      location: w.location,
      imageUrl: w.imageUrl ?? w.giveaway.imageUrl ?? undefined,
      giveawayId: w.giveaway.id,
      giveawayTitle: w.giveaway.title,
      date: w.giveaway.endDate,
    })),
  })
}

const PartnerPage = () => {
  const {partner, activeGiveaways, recentWinners} = useSuperLoaderData<typeof loader>();

  return (
    <VStack gap={8} py={8} alignItems="stretch">
      <Container width="full">
        <VStack gap={8}>
          <h1 className={h1()}>
            {partner.name}
          </h1>
          {activeGiveaways.length > 0 && (
            <Box width="full">
              <h2 className={h2()}>
                {activeGiveaways.length === 1 ? 'Active Promotion' : 'Active Promotions'}
              </h2>
              <Grid columns={{base: 1, md: 3}} width="full">
                {activeGiveaways.map(promotion => (
                  <PromotionCard
                    key={promotion.id}
                    link={`/promotions/${promotion.id}/${partner.id}`}
                    {...promotion}
                  />
                ))}
              </Grid>
            </Box>
          )}
        </VStack>
      </Container>
      <Benefits/>
      <Container width="full">
        <RecentWinners winners={recentWinners} />
      </Container>
    </VStack>
  );
};

export default PartnerPage;
